import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  DollarSign,
  Battery,
  Info,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Share2,
  Download,
  BarChart3,
  Zap,
  MapPin,
  Layers,
  AlertTriangle,
  ArrowRightLeft
} from 'lucide-react';
import { batteriesAPI } from '../../services/garageApi';
import ManageStockModal from '../common/ManageStockModal';
import UpdatePriceModal from '../common/UpdatePriceModal';
import ProductHistoryTimeline from '../common/ProductHistoryTimeline';
import ProductAnalytics from '../common/ProductAnalytics';
import { shareProduct } from '../../utils/shareUtils';
import { exportProduct } from '../../utils/exportUtils';

const BatteryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [battery, setBattery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [shareToast, setShareToast] = useState('');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);


  const fetchBattery = useCallback(async () => {
    try {
      const response = await batteriesAPI.show(id);

      // Extract battery data from response
      let batteryData = null;

      if (response.data) {
        // Check for success wrapper
        if (response.data.success && response.data.data) {
          batteryData = response.data.data;
        }
        // Direct data wrapper
        else if (response.data.data) {
          batteryData = response.data.data;
        }
        // Direct battery object
        else if (response.data.id) {
          batteryData = response.data;
        }
      }

      // Process battery data to add computed fields
      if (batteryData) {
        if (batteryData.inventories && Array.isArray(batteryData.inventories)) {
          batteryData.quantity = batteryData.inventories.reduce((sum, inv) => sum + (inv.quantity || 0), 0);
          batteryData.has_unallocated = batteryData.inventories.some(
            inv => inv.zone?.is_system && inv.quantity > 0
          );
        }

        if (!batteryData.quantity && batteryData.inventory_quantity) {
          batteryData.quantity = batteryData.inventory_quantity;
        }
      }

      setBattery(batteryData);
    } catch (error) {
      console.error('Error fetching battery:', error);
      setBattery(null);
    } finally {
      setLoading(false);
    }

  },[id]);

  useEffect(() => {
    fetchBattery();
  }, [fetchBattery]);


  const handleDelete = async () => {
    try {
      await batteriesAPI.delete(id);
      navigate('/batteries', {
        state: { message: 'Battery product deleted successfully!' }
      });
    } catch (error) {
      console.error('Error deleting battery:', error);
      alert('Failed to delete battery. Please try again.');
    }
  };

  const getStockStatus = (quantity) => {
    const qty = parseInt(quantity || 0);
    if (qty === 0) return { text: 'Out of Stock', color: 'red', icon: XCircle };
    if (qty < 10) return { text: 'Low Stock', color: 'yellow', icon: AlertCircle };
    return { text: 'In Stock', color: 'green', icon: CheckCircle };
  };

  // Helper function to safely get the name from an object or string
  const getNameFromField = (field) => {
    if (!field) return 'N/A';
    if (typeof field === 'string') return field;
    if (typeof field === 'object' && field.name) return field.name;
    return 'N/A';
  };

  if (loading) {
    return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );
  }

  if (!battery) {
    return (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Battery className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Battery not found</h3>
          <Link
              to="/batteries"
              className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Back to Batteries
          </Link>
        </div>
    );
  }

  // Extract related data safely
  const brand = battery.battery_brand || battery.brand || {};
  const madeIn = battery.made_in || battery.madeIn || {};
  const location = battery.location || {};
  const quantity = battery.quantity || battery.inventory_quantity || 0;

  const stockStatus = getStockStatus(quantity);
  const StockIcon = stockStatus.icon;

  const costPrice = parseFloat(battery.cost_price || 0);
  const sellingPrice = parseFloat(battery.selling_price || 0);
  const profitMargin = costPrice > 0
      ? ((sellingPrice - costPrice) / costPrice * 100).toFixed(2)
      : 0;

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Info },
    { id: 'specifications', name: 'Specifications', icon: Battery },
    { id: 'pricing', name: 'Pricing & Inventory', icon: DollarSign },
    { id: 'history', name: 'History', icon: Clock },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 }
  ];

  return (
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                  to="/batteries"
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{battery.battery_name}</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {getNameFromField(brand)} • {battery.ampere} AH
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  const result = await shareProduct(battery.battery_name);
                  if (result.success) {
                    setShareToast('Link copied to clipboard!');
                    setTimeout(() => setShareToast(''), 3000);
                  }
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              <div className="relative">
                <button
                  onClick={() => setExportMenuOpen(!exportMenuOpen)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
                {exportMenuOpen && (
                  <div className="absolute right-0 mt-1 w-32 bg-white border rounded-lg shadow-lg z-10">
                    <button onClick={() => { exportProduct(battery, 'battery', 'csv'); setExportMenuOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50">CSV</button>
                    <button onClick={() => { exportProduct(battery, 'battery', 'pdf'); setExportMenuOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50">PDF</button>
                  </div>
                )}
              </div>
              <Link
                  to={`/batteries/${id}/edit`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Link>
              <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Image & Key Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start gap-6">
                <div className="w-48 h-48 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                  <Battery className="w-32 h-32 text-blue-700" />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                  <span className={`px-3 py-1 bg-${stockStatus.color}-100 text-${stockStatus.color}-700 text-sm font-medium rounded-full flex items-center gap-1`}>
                    <StockIcon className="w-4 h-4" />
                    {stockStatus.text}
                  </span>
                    {battery.active ? (
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                      Active
                    </span>
                    ) : (
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                      Inactive
                    </span>
                    )}
                    {battery.reversed && (
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                      Reversed
                    </span>
                    )}
                  </div>

                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{battery.battery_name}</h2>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Brand:</span>
                      <span className="ml-2 font-medium">{getNameFromField(brand)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Ampere:</span>
                      <span className="ml-2 font-medium">{battery.ampere} AH</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Type:</span>
                      <span className="ml-2 font-medium capitalize">{getNameFromField(battery.battery_type)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Shape:</span>
                      <span className="ml-2 font-medium capitalize">
                      {battery.battery_shape || battery.product_shape || 'N/A'}
                    </span>
                    </div>
                  </div>

                  {/* Battery Type Badge */}
                  <div className="flex items-center gap-3 mt-4">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm font-medium text-gray-700">
                      {getNameFromField(battery.battery_type).toUpperCase()} Battery
                    </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs Content */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {/* Tab Navigation */}
              <div className="border-b border-gray-100">
                <div className="flex items-center gap-1 p-1">
                  {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                                activeTab === tab.id
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="hidden sm:inline">{tab.name}</span>
                        </button>
                    );
                  })}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm text-gray-600">Battery Name</p>
                              <p className="font-medium">{battery.battery_name}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Date Received</p>
                              <p className="font-medium">{battery.date_received || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Made In</p>
                              <p className="font-medium">{getNameFromField(madeIn)}</p>
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm text-gray-600">Brand</p>
                              <p className="font-medium">{getNameFromField(brand)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Location</p>
                              <p className="font-medium">{getNameFromField(location)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Status</p>
                              <p className="font-medium">
                                {battery.active ? 'Active' : 'Inactive'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {battery.notes && (
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes</h3>
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-sm text-gray-700">{battery.notes}</p>
                            </div>
                          </div>
                      )}
                    </div>
                )}

                {activeTab === 'specifications' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical Specifications</h3>
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Ampere Rating</span>
                              <span className="font-medium">{battery.ampere} AH</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Battery Type</span>
                              <span className="font-medium capitalize">{getNameFromField(battery.battery_type)}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Shape</span>
                              <span className="font-medium capitalize">
                            {battery.battery_shape || battery.product_shape || 'N/A'}
                          </span>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Terminal Type</span>
                              <span className="font-medium">{battery.reversed ? 'Reversed' : 'Standard'}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Brand</span>
                              <span className="font-medium">{getNameFromField(brand)}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Origin</span>
                              <span className="font-medium">{getNameFromField(madeIn)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                )}

                {activeTab === 'pricing' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Cost Price</p>
                            <p className="text-2xl font-bold text-gray-900">${costPrice.toFixed(2)}</p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-4">
                            <p className="text-sm text-blue-700 mb-1">Selling Price</p>
                            <p className="text-2xl font-bold text-blue-900">${sellingPrice.toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="mt-4 bg-green-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-green-700">Profit Margin</p>
                              <p className="text-2xl font-bold text-green-900">
                                ${(sellingPrice - costPrice).toFixed(2)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-green-700">Margin %</p>
                              <p className="text-2xl font-bold text-green-900">{profitMargin}%</p>
                            </div>
                          </div>
                        </div>

                        {/* Tax & Final Price */}
                        <div className="mt-4 grid grid-cols-2 gap-4">
                          <div className="bg-yellow-50 rounded-lg p-4">
                            <p className="text-sm text-yellow-700 mb-1">Tax Rate</p>
                            <p className="text-2xl font-bold text-yellow-900">{parseFloat(battery.tax_rate || 0).toFixed(2)}%</p>
                            <p className="text-xs text-yellow-600 mt-1">{battery.is_tax_inclusive ? 'Included in price' : 'Added to price'}</p>
                          </div>
                          <div className="bg-indigo-50 rounded-lg p-4">
                            <p className="text-sm text-indigo-700 mb-1">Final Price (incl. tax)</p>
                            <p className="text-2xl font-bold text-indigo-900">
                              ${(battery.is_tax_inclusive ? sellingPrice : sellingPrice * (1 + parseFloat(battery.tax_rate || 0) / 100)).toFixed(2)}
                            </p>
                            <p className="text-xs text-indigo-600 mt-1">{battery.is_tax_inclusive ? 'Tax included in selling price' : `Selling Price + ${battery.tax_rate || 0}% tax`}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">Inventory Status</h3>
                          <button
                            onClick={() => setStockModalOpen(true)}
                            className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors flex items-center gap-2 text-sm font-medium"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                            Manage Stock
                          </button>
                        </div>

                        {battery.has_unallocated && (
                          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 text-yellow-700 text-sm">
                            <AlertTriangle className="w-4 h-4" />
                            Some stock is pending allocation to zones.
                          </div>
                        )}

                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <p className="text-sm text-gray-600">Total Stock</p>
                              <p className="text-2xl font-bold text-gray-900">{quantity} units</p>
                            </div>
                            <StockIcon className={`w-12 h-12 text-${stockStatus.color}-500`} />
                          </div>
                          <div className="pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Total Inventory Value</span>
                              <span className="text-lg font-bold text-gray-900">
                                ${(quantity * sellingPrice).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Zone-aware distribution table */}
                        {battery.inventories && battery.inventories.length > 0 && (
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Zone</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {battery.inventories.map((inv) => {
                                  const isUnallocated = inv.zone?.is_system;
                                  return (
                                    <tr key={inv.id} className={isUnallocated ? 'bg-yellow-50' : ''}>
                                      <td className="px-4 py-2.5 flex items-center gap-2">
                                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                        {inv.location?.name || 'Unknown'}
                                      </td>
                                      <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-2">
                                          <Layers className="w-3.5 h-3.5 text-gray-400" />
                                          {inv.zone?.name || 'No Zone'}
                                        </div>
                                      </td>
                                      <td className="px-4 py-2.5 text-right font-medium">{inv.quantity}</td>
                                      <td className="px-4 py-2.5">
                                        {isUnallocated ? (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                            <AlertTriangle className="w-3 h-3" />
                                            Needs Allocation
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                            <CheckCircle className="w-3 h-3" />
                                            Allocated
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity History</h3>
                      <ProductHistoryTimeline productType="battery" productId={id} />
                    </div>
                )}

                {activeTab === 'analytics' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Analytics</h3>
                      <ProductAnalytics productType="battery" productId={id} />
                    </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Quick Info */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm text-gray-600">Stock Level</span>
                  </div>
                  <span className="font-semibold">{quantity}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-600">Value</span>
                  </div>
                  <span className="font-semibold">${(quantity * sellingPrice).toFixed(2)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-sm text-gray-600">Margin</span>
                  </div>
                  <span className="font-semibold text-green-600">{profitMargin}%</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="text-sm text-gray-600">Power</span>
                  </div>
                  <span className="font-semibold">{battery.ampere} AH</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                <Link
                    to={`/batteries/${id}/edit`}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Product
                </Link>
                <button
                    onClick={() => setStockModalOpen(true)}
                    className="w-full px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  Manage Stock
                </button>
                <button
                  onClick={() => setPriceModalOpen(true)}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <DollarSign className="w-4 h-4" />
                  Update Price
                </button>
              </div>
            </div>

            {/* Related Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Product ID</p>
                  <p className="font-medium">#{battery.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="font-medium">
                    {battery.created_at ? new Date(battery.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="font-medium">
                    {battery.updated_at ? new Date(battery.updated_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Share Toast */}
        {shareToast && (
          <div className="fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm">
            {shareToast}
          </div>
        )}

        {/* Update Price Modal */}
        <UpdatePriceModal
          isOpen={priceModalOpen}
          onClose={() => setPriceModalOpen(false)}
          productId={battery?.id}
          productType="battery"
          currentCostPrice={battery?.cost_price}
          currentSellingPrice={battery?.selling_price}
          currentTaxRate={battery?.tax_rate}
          currentIsTaxInclusive={battery?.is_tax_inclusive}
          onPriceUpdated={() => fetchBattery()}
        />

        {/* Manage Stock Modal */}
        <ManageStockModal
          isOpen={stockModalOpen}
          onClose={() => setStockModalOpen(false)}
          productIds={battery ? [battery.id] : []}
          productLabel={battery?.battery_name || ''}
          productType="battery"
          onStockMoved={() => fetchBattery()}
        />

        {/* Delete Modal */}
        {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Battery Product</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete "{battery.battery_name}"? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                      onClick={() => setShowDeleteModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                      onClick={handleDelete}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
        )}
      </div>
  );
};

export default BatteryDetail;